import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { useBrandKits } from '@/hooks/useBrandKits';
import { unitsForConceptChannel } from '@/lib/briefs/concept-copy-state';
import type { ConceptDeliveryTarget, CopyField } from '@/lib/concept-copy/platform-limits';
import { checkFieldLimits } from '@/lib/concept-copy/platform-limits';
import {
  type ConceptCopyVariant,
  type CopyVariantCardData,
  toCopyVariantCardData,
} from '@/lib/concept-copy/types';
import { ownerScopedKey, resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { conceptCopyVariantService } from '@/services/data/ConceptCopyVariantService';

/**
 * The Concept Workbench's Content section, as data.
 *
 * `ad_units` and `project_task_delivery_intents` are both ACCOUNT-scoped
 * (`src/lib/identity/table-scope.generated.ts`), so this is the resolve-then-
 * fetch shape `useAdUnits`/`useBrandHandles` established: the raw session id
 * would silently read zero rows for an account member, in dev, in review, and
 * in every test — vitest mocks Supabase and never evaluates an RLS policy. The
 * RESOLVED id is also part of the query key, or two members of one account
 * would keep separate caches of identical data.
 */
export function useConceptCopyVariants(conceptId: string | null | undefined) {
  const { user, session } = useAuth();
  const { activeKit, isActiveKitPending } = useBrandKits();
  const queryClient = useQueryClient();

  const { data: ownerId } = useQuery({
    queryKey: ['account-owner-id', user?.id],
    queryFn: () => resolveOwnerId(user!.id),
    enabled: Boolean(user?.id),
    staleTime: 5 * 60 * 1000,
  });

  const brandKitId = activeKit?.id ?? null;
  const enabled = Boolean(ownerId && conceptId && brandKitId && !isActiveKitPending);
  const variantsKey = ownerScopedKey(
    ['concept-copy-variants', brandKitId ?? 'no-workspace', conceptId ?? 'none'],
    ownerId,
  );
  const targetsKey = ownerScopedKey(
    ['concept-delivery-targets', brandKitId ?? 'no-workspace', conceptId ?? 'none'],
    ownerId,
  );

  const variantsQuery = useQuery({
    queryKey: variantsKey,
    enabled,
    queryFn: () => conceptCopyVariantService.list(conceptId!, ownerId!, brandKitId!),
  });

  const targetsQuery = useQuery({
    queryKey: targetsKey,
    enabled,
    queryFn: () => conceptCopyVariantService.listDeliveryTargets(conceptId!, ownerId!, brandKitId!),
    staleTime: 60 * 1000,
  });

  // Memoised so the `??  []` does not hand a new array identity to the
  // callbacks below on every render.
  const targets = useMemo<ConceptDeliveryTarget[]>(
    () => targetsQuery.data ?? [],
    [targetsQuery.data],
  );

  const generateMutation = useMutation({
    mutationFn: (input: {
      commandId: string;
      count?: number;
      anchorAdUnitId?: string;
      product?: string;
    }) => {
      if (!conceptId) throw new Error('No concept is open.');
      if (!brandKitId) throw new Error('Select a workspace before generating copy.');
      const accessToken = session?.access_token;
      if (!accessToken) throw new Error('You must be signed in to generate copy.');
      return conceptCopyVariantService.generate({
        conceptId,
        brandKitId,
        commandId: input.commandId,
        accessToken,
        ...(input.count === undefined ? {} : { count: input.count }),
        ...(input.anchorAdUnitId ? { anchorAdUnitId: input.anchorAdUnitId } : {}),
        ...(input.product ? { product: input.product } : {}),
      });
    },
    onSuccess: (result) => {
      // Pending/refused responses wrote no completed option set. Invalidating
      // would make either state look like an empty successful generation.
      if ('state' in result) return;
      void queryClient.invalidateQueries({ queryKey: variantsKey });
    },
  });

  /**
   * "More like this" — regeneration anchored on ONE option.
   *
   * A separate entry point rather than an optional argument on `generate`, so a
   * call site cannot lose the anchor by forgetting to pass it. The route
   * refuses outright if the anchor cannot be read, and discards options that
   * come back as the anchor verbatim, so an anchor that is ignored surfaces as
   * fewer options rather than as duplicates the user paid for.
   */
  const moreLikeThis = useCallback(
    (anchorAdUnitId: string, count: number | undefined, commandId: string) =>
      generateMutation.mutateAsync({
        anchorAdUnitId,
        commandId,
        ...(count === undefined ? {} : { count }),
      }),
    [generateMutation],
  );

  /** Exactly `CopyVariantCard`'s data props for one variant/field pair. */
  const cardData = useCallback(
    (variant: ConceptCopyVariant, field: CopyField): CopyVariantCardData | null =>
      toCopyVariantCardData(variant, field, targets),
    [targets],
  );

  /** Every platform a variant's field overruns, and by how much. */
  const limitBreaches = useCallback(
    (variant: ConceptCopyVariant, field: CopyField) => {
      const value = variant.fields.find((entry) => entry.field === field);
      if (!value) return [];
      return checkFieldLimits(value.text, field, targets);
    },
    [targets],
  );

  // The options this Concept offers are the copy for the channels it is set to run on, read through
  // the same function the Brief page and the Concept page use. Copy saved for another channel
  // stays saved and is not offered, so the canvas and the preview open on this Concept's channel.
  // Until the channel has been read there is no way to know which options belong, so none are
  // offered: a pending or failed channel read must not fall back to every saved option, which
  // opens the preview on copy written for another channel.
  const variants = useMemo(
    () =>
      targetsQuery.data === undefined
        ? []
        : unitsForConceptChannel(variantsQuery.data ?? [], targetsQuery.data),
    [variantsQuery.data, targetsQuery.data],
  );

  return {
    variants,
    targets,
    isLoading: variantsQuery.isPending || targetsQuery.isPending,
    error: variantsQuery.error ?? targetsQuery.error,
    refetch: variantsQuery.refetch,
    generate: generateMutation.mutateAsync,
    isGenerating: generateMutation.isPending,
    moreLikeThis,
    cardData,
    limitBreaches,
  };
}
